/**
 * The series window's mover (T-A4, "Przesuń sesję") — one occurrence given another day or hour
 * while the rest of the series keeps its own.
 *
 * A week strip picks the day (the arrows page it); the day itself — its calendar and its free
 * hours, clear of every session and block — is `DayOpenings`, the same view "Przełóż" shows. Any
 * day and hour may be chosen, a sibling occurrence's included (owner, 25 September 2026): the
 * series list marks such a pair "koliduje: sesja N tej serii" and never blocks it (R9).
 *
 * Both pickers are native radio groups, so the arrows walk them; "Gotowe" and "Wróć do listy"
 * return to the list.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';
import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { dayMonth, shortWeekday } from '../model/calendarWords';
import {
  moveOccurrence,
  weekOf,
  type SeriesOccurrence,
  type SeriesPlan,
} from '../model/recurringSeries';
import { shiftDay, type CivilDay } from '../model/trainerClock';
import compose from './ComposeForm.module.css';
import { DayOpenings } from './DayOpenings';
import styles from './SeriesDialog.module.css';
import side from './SidePanel.module.css';

interface OccurrenceMoverProps {
  readonly plan: SeriesPlan;
  readonly occurrence: SeriesOccurrence;
  readonly total: number;
  readonly zone: string;
  readonly nowMs: number;
  readonly onMove: (plan: SeriesPlan) => void;
  readonly onDone: () => void;
}

export function OccurrenceMover({
  plan,
  occurrence,
  total,
  zone,
  nowMs,
  onMove,
  onDone,
}: OccurrenceMoverProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const back = useRef<HTMLButtonElement>(null);
  const dayGroup = useId();
  const [weekAnchor, setWeekAnchor] = useState(occurrence.day);
  useEffect(() => {
    back.current?.focus();
  }, []);

  const day = occurrence.day;
  const clash = occurrence.conflicts?.[0] ?? null;
  const moveTo = (to: { day: CivilDay; minutes: number }) => {
    onMove(moveOccurrence(plan, occurrence.key, to));
  };
  return (
    <div className={styles.mover}>
      <div className={styles.moverTop}>
        <button ref={back} type="button" className={side.back} onClick={onDone}>
          <ChevronGlyph className={side.backGlyph} direction="left" />
          {t('web:booking.series.mover.back')}
        </button>
        <span className={styles.until}>
          {t('web:booking.series.mover.position', { number: occurrence.number, total })}
        </span>
      </div>

      <div className={styles.moverHead}>
        <div>
          <span className={styles.eyebrow}>{t('web:booking.series.mover.eyebrow')}</span>
          <h3 className={styles.liveTitle}>
            {t('web:booking.series.mover.title', {
              day: `${shortWeekday(day, locale)} ${dayMonth(day)}`,
            })}
          </h3>
        </div>
        <span className={styles.weekNav}>
          <button
            type="button"
            className={styles.weekStep}
            aria-label={t('web:booking.series.mover.previousWeek')}
            onClick={() => {
              setWeekAnchor(shiftDay(weekAnchor, -7));
            }}
          >
            <ChevronGlyph className={side.backGlyph} direction="left" />
          </button>
          <button
            type="button"
            className={styles.weekStep}
            aria-label={t('web:booking.series.mover.nextWeek')}
            onClick={() => {
              setWeekAnchor(shiftDay(weekAnchor, 7));
            }}
          >
            <ChevronGlyph className={side.backGlyph} direction="right" />
          </button>
        </span>
      </div>

      <fieldset className={styles.dayStrip}>
        <legend className={hidden.srOnly}>{t('web:booking.series.mover.days')}</legend>
        {weekOf(weekAnchor).map((entry) => (
          <label
            key={entry}
            className={`${String(styles.dayChip)} ${entry === day ? String(styles.dayChipOn) : ''}`}
          >
            <input
              className={hiddenControl.control}
              type="radio"
              name={dayGroup}
              checked={entry === day}
              onChange={() => {
                moveTo({ day: entry, minutes: occurrence.minutes });
              }}
            />
            <span className={styles.dayChipWeekday}>{shortWeekday(entry, locale)}</span>
            <span className={styles.dayChipDate}>{dayMonth(entry)}</span>
          </label>
        ))}
      </fieldset>

      <DayOpenings
        day={day}
        zone={zone}
        nowMs={nowMs}
        durationMinutes={plan.durationMinutes}
        exceptId={null}
        candidate={{ minutes: occurrence.minutes, clash: clash !== null }}
        onPick={(minutes) => {
          moveTo({ day, minutes });
        }}
        aside={
          <>
            {clash === null ? null : (
              <p className={styles.conflictBanner} role="status">
                <WarningGlyph className={compose.noteGlyph} />
                {t('web:booking.series.mover.taken', {
                  name: clash.clientName ?? t('web:booking.unnamedClient'),
                })}
              </p>
            )}
            <p className={styles.hint} aria-live="polite">
              {t('web:booking.series.mover.hint')}
            </p>
            <button type="button" className={styles.done} onClick={onDone}>
              {t('web:booking.series.mover.done')}
            </button>
          </>
        }
      />
    </div>
  );
}
